'use client';

import { Header } from '@/components/header';
import { Footer } from '@/components/footer';
import { Breadcrumb } from '@/components/breadcrumb';
import { ContactModal } from '@/components/contact-modal';
import { useState, useEffect } from 'react';

interface FaqItem {
  question: string;
  answer: string;
}

interface FaqCategory {
  category: string;
  questions: FaqItem[];
}

export default function FaqPage() {
  const [faqs, setFaqs] = useState<FaqCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    fetch('/api/faqs')
      .then(res => res.json())
      .then(data => {
        setFaqs(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('获取FAQ失败:', err);
        setLoading(false);
      });
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-1 pt-16">
        <Breadcrumb items={[{ label: '常见问题', href: '/faq' }]} />
        
        {/* 页面标题 */}
        <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl font-bold mb-4">常见问题</h1>
            <p className="text-xl text-blue-100">
              解答您关于标准参编、科技成果评价、贸易增量等服务的疑问
            </p>
          </div>
        </section>

        {/* FAQ内容 */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="text-center py-12">加载中...</div>
            ) : faqs.length === 0 ? (
              <div className="text-center py-12 text-gray-500">暂无FAQ数据</div>
            ) : (
              <div className="space-y-12">
                {faqs.map((section, index) => (
                  <div key={index}>
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-600">
                      {section.category}
                    </h2>
                    <div className="space-y-4">
                      {section.questions.map((item, qIndex) => (
                        <details key={qIndex} className="group bg-white rounded-lg shadow-sm border border-gray-200">
                          <summary className="flex justify-between items-center font-medium text-gray-900 p-5 cursor-pointer hover:bg-gray-50 rounded-lg">
                            <span className="flex items-center">
                              <span className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3">
                                Q
                              </span>
                              {item.question}
                            </span>
                            <span className="transition group-open:rotate-180">
                              <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </span>
                          </summary>
                          <div className="p-5 pt-0 text-gray-600">
                            <div className="flex items-start mt-4">
                              <span className="bg-green-100 text-green-600 rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3 flex-shrink-0">
                                A
                              </span>
                              <div>{item.answer}</div>
                            </div>
                          </div>
                        </details>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* 联系我们 */}
        <section className="py-16 bg-gray-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">还有其他问题？</h2>
            <p className="text-gray-600 mb-8">
              如果您没有找到想要的答案，请随时联系我们的客服团队
            </p>
            <button
              onClick={() => setContactOpen(true)}
              className="inline-flex items-center px-8 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              联系我们
            </button>
          </div>
        </section>
      </main>
      <Footer />
      <ContactModal open={contactOpen} onOpenChange={setContactOpen} />
    </div>
  );
}
